/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext,
    IRequestContext,
    getCatalogId
} from '@msdyn365-commerce/core';
import { AsyncResult, CategoryPathResult, IQueryResultSettings, ProductPrice, ProductSearchResult, ProjectionDomain } from '@msdyn365-commerce/retail-proxy';
import {
    buildCacheKeyWithUrlTokens,
    IQueryResultSettingsModuleConfig,
    OrderTemplate,
    QueryResultSettingsProxy,
    generateImageUrl
} from '@msdyn365-commerce-modules/retail-actions';
import { IProductRefinerHierarchy } from '@msdyn365-commerce/commerce-entities';
import {
    getFullOrderTemplateActionDataAction as getFullOrderTemplate,
    GetFullOrderTemplateInput
} from './get-dlvry-full-order-template';
import { getPaginatedOrderTemplateLinesAction, GetPaginatedOrderTemplateLinesInput } from '../../../actions/get-paginated-order-template-lines';
import { getProductRefinerHierarchy } from '../../dlvry-search-result-container/actions/get-product-refiner-hierarchy';
import { getActivePricesAsync, getCategoryPathsAsync, searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getLocale } from '../../../shared-utils/attributes/locale';
import { getDiscountBadge } from '../../../shared-utils/badge-utils';
import { SimpleProductExt } from '../../../shared-utils/types';
import { getTemperatureZone } from '../../../shared-utils/attributes/get-temperature-zone';

/**
 * Input class for getActiveOrderTemplateLines data action.
 */
export class ActiveOrderTemplateLinesInput implements IActionInput {
    public requestContext: IRequestContext;

    public orderTemplateId: string;

    public queryResultSettings: IQueryResultSettings;

    constructor(requestContext: IRequestContext, config: IQueryResultSettingsModuleConfig) {
        if (!requestContext.query?.id) {
            throw new Error('Failed to get order template id from the query.');
        }
        if (!config) {
            throw new Error('Failed to get order template module configuration');
        }
        this.orderTemplateId = requestContext.query.id;
        this.queryResultSettings = QueryResultSettingsProxy.fromModuleData(requestContext, config).QueryResultSettings;

        this.queryResultSettings.count = true;
        this.requestContext = requestContext;
    }

    public getCacheKey = (): string => `${buildCacheKeyWithUrlTokens('ActiveOrderTemplateLines', this.requestContext)}-${this.orderTemplateId}`;

    public getCacheObjectType = (): string => 'ActiveOrderTemplateLines';

    public dataCacheType = (): CacheType => 'none';

    public shouldCacheOutput = () => false;
}

/**
 * Calls the Retail API and returns a ProductList object based on the passed GetCartInput.
 * @param input
 * @param context
 */
export async function getActiveOrderTemplateLines(input: ActiveOrderTemplateLinesInput, context: IActionContext): Promise<OrderTemplate> {
    const orderTemplate = await getFullOrderTemplate(new GetFullOrderTemplateInput(input.orderTemplateId, input.queryResultSettings), context);
    const inputLines = new GetPaginatedOrderTemplateLinesInput(orderTemplate.productList.Id, {});
    inputLines.getPrice = false;
    inputLines.getRating = false;
    const lines = await getPaginatedOrderTemplateLinesAction(inputLines, context);

    // Filter out products that are out of assortment and sort by name
    orderTemplate.orderTemplateLines = lines.lines
        .filter((line) => line.fullProduct);
    // Update total lines after filtering
    orderTemplate.totalLines = orderTemplate.orderTemplateLines.length;

    const catalogId = getCatalogId(context.requestContext);
    const projectDomain: ProjectionDomain = { ChannelId: +context.requestContext.apiSettings.channelId, CatalogId: catalogId };
    const ids = orderTemplate.orderTemplateLines.map((line) => line.fullProduct?.ProductDetails.RecordId).filter(Boolean) as number[];

    let refinersPromise: Promise<IProductRefinerHierarchy[]> | undefined;
    let attributesPromise: AsyncResult<ProductSearchResult[]> | undefined;
    let productPricesPromise: Promise<ProductPrice[]> | undefined;
    let categoryPathsPromise: Promise<CategoryPathResult[]> | undefined;

    if (ids.length > 0) {
        refinersPromise = getProductRefinerHierarchy(
            {
                Ids: ids,
                Context: {
                    ChannelId: input.requestContext.apiSettings.channelId,
                    CatalogId: input.requestContext.apiSettings.catalogId
                }
            },
            input.queryResultSettings,
            context
        );

        attributesPromise = searchByCriteriaAsync({ callerContext: context }, {
            Context: {
                ChannelId: input.requestContext.apiSettings.channelId,
                CatalogId: input.requestContext.apiSettings.catalogId
            },
            Ids: ids,
            IncludeAttributes: true
        });

        // Get available cached active prices. If not available client will have to retrieve it
        productPricesPromise = getActivePricesAsync({ callerContext: context },
            projectDomain,
            lines.lines.map((line) => line.fullProduct?.ProductDetails.RecordId).filter(Boolean).slice(0, input.queryResultSettings.Paging?.Top || 10) as number[],
            new Date(),
            null,
            null,
            true,
            false,
            false);

        categoryPathsPromise = getCategoryPathsAsync({ callerContext: context },
            context.requestContext.apiSettings.channelId,
            context.requestContext.apiSettings.catalogId,
            ids.map(id => ({ ProductId: id }))
        );
    }

    const [refiners, attributes, productPrices, categoryPaths] = await Promise.all([refinersPromise, attributesPromise, productPricesPromise, categoryPathsPromise]);

    (orderTemplate as any).refiners = refiners;

    orderTemplate.orderTemplateLines.forEach((line) => {
        if (!line.fullProduct) {
            return;
        }

        const price = productPrices?.find((price) => price.ProductId === line.fullProduct?.ProductDetails.RecordId);
        const discountBadge = getDiscountBadge(price);

        // Set Price to 0 if price wasn't found in cache and client will have to retrieve it
        line.fullProduct.ProductDetails.Price = price?.CustomerContextualPrice || 0;
        line.fullProduct.ProductDetails.BasePrice = price?.BasePrice || 0;

        const attributeLocale = getLocale(context.requestContext.locale);
        const brand = attributes?.find(x => x.RecordId === line.fullProduct!.ProductDetails.RecordId)?.AttributeValues?.find(x => x.Name === attributeLocale.brand)?.TextValue;
        const manufacturer = attributes?.find(x => x.RecordId === line.fullProduct!.ProductDetails.RecordId)?.AttributeValues?.find(x => x.Name === attributeLocale.manufacturer)?.TextValue;
        const transit = attributes?.find(x => x.RecordId === line.fullProduct!.ProductDetails.RecordId)?.AttributeValues?.find(x => x.Name === attributeLocale.transit)?.TextValue;
        const temperatureZone = getTemperatureZone(context.requestContext.locale, attributes?.find(x => x.RecordId === line.fullProduct!.ProductDetails.RecordId)?.AttributeValues);

        if (brand) {
            (line.fullProduct.ProductDetails as SimpleProductExt).Brand = brand;
        }

        if (manufacturer) {
            (line.fullProduct.ProductDetails as SimpleProductExt).Manufacturer = manufacturer;
        }

        const categoryPath = categoryPaths?.find(x => x.ProductId === line.fullProduct?.ProductDetails.RecordId);

        if (categoryPath) {
            (line.fullProduct.ProductDetails as SimpleProductExt).CategoryPaths = categoryPath.CategoryPath;
        }

        if (discountBadge) {
            (line.fullProduct.ProductDetails as SimpleProductExt).Badge = discountBadge;
        }

        if (price) {
            (line.fullProduct.ProductDetails as SimpleProductExt).ProductPrice = price;
        }

        if (transit) {
            (line.fullProduct.ProductDetails as SimpleProductExt).Transit = transit === '1';
        }

        if (temperatureZone) {
            (line.fullProduct.ProductDetails as SimpleProductExt).TemperatureZone = temperatureZone;
        }

        // Get primary image url from attributes search results if it's not already set
        if (!line.fullProduct.ProductDetails.PrimaryImageUrl) {
            let primaryImageUrl = attributes?.find(x => x.RecordId === line.fullProduct!.ProductDetails.RecordId)?.PrimaryImageUrl;
            if (primaryImageUrl) {
                primaryImageUrl = generateImageUrl(primaryImageUrl, context.requestContext.apiSettings);
            }

            line.fullProduct.ProductDetails.PrimaryImageUrl = primaryImageUrl;
        }
    });
    return orderTemplate;
}

export const getActiveOrderTemplateLinesDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/order-template/order-template/get-order-template-lines',
    action: <IAction<OrderTemplate>>getActiveOrderTemplateLines,
    input: (context: ICreateActionContext) => {
        if (!context.config) {
            throw new Error('Failed to get order template module configuration');
        }
        return new ActiveOrderTemplateLinesInput(context.requestContext, context.config);
    }
});

export default getActiveOrderTemplateLinesDataAction;
